import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { templateApi, dictionaryApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Table, Modal, Tabs, Field } from "../../Components/UI/ui";
import { GATE, fmtDate } from "../../lib/lab";
import ScanTemplates from "../Radiology/ScanTemplates";

// The lab's definitions: pathology tests (results doc §2) and radiology scan
// templates (radiology doc §2). Admin+ edit; owner deletes.
const Templates = () => {
  const [params, setParams] = useSearchParams();
  const area = params.get("area") === "scans" ? "scans" : "tests";
  return (
    <PathologyOrScans area={area} setArea={(a) => setParams(a === "scans" ? { area: "scans" } : {})} />
  );
};

const PathologyOrScans = ({ area, setArea }) => {
  const { can } = useAuth();
  const { toast, invalidateTemplates } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("true");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromCat, setFromCat] = useState(false);
  const canWrite = can(...GATE.templatesWrite);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (active) params.isActive = active;
      if (category) params.category = category;
      const res = await templateApi.list(params);
      setRows(res?.data || []);
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [search, active, category, toast]);

  useEffect(() => {
    const h = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, search]);

  const categories = [...new Set(rows.map((r) => r.category).filter(Boolean))].sort();

  const columns = [
    { key: "name", title: "Test", render: (r) => (<><div className="primary">{r.name}</div><div className="sub">{r.shortName || ""}{r.specimenType ? ` · ${r.specimenType}` : ""}</div></>) },
    { key: "category", title: "Category", render: (r) => r.category || <span className="sub">—</span> },
    { key: "parameterCount", title: "Parameters", align: "right" },
    { key: "source", title: "Source", render: (r) => <span className="sub">{r.source?.refType === "TestPanel" ? "Global panel" : r.source?.refType === "TestName" ? "Global test" : r.source?.refType || "manual"}</span> },
    { key: "version", title: "Version", render: (r) => `v${r.version}` },
    { key: "updatedAt", title: "Updated", render: (r) => fmtDate(r.updatedAt) },
    { key: "isActive", title: "Status", render: (r) => <Badge kind={r.isActive ? "on" : "off"}>{r.isActive ? "Active" : "Inactive"}</Badge> },
  ];

  return (
    <Page
      title="Definitions"
      subtitle={area === "scans" ? "What each scan reports: the text a radiologist starts from" : "What each test produces: parameters, units, reference ranges"}
      actions={area === "tests" && canWrite && (
        <>
          <Button onClick={() => navigate("/tests/new")}>Blank template</Button>
          <Button variant="primary" onClick={() => setFromCat(true)}><IoMdAdd /> From global catalogue</Button>
        </>
      )}
    >
      <Tabs
        tabs={[{ id: "tests", label: "Pathology tests" }, { id: "scans", label: "Radiology scans" }]}
        value={area}
        onChange={setArea}
      />
      {area === "scans" ? <ScanTemplates /> : (<>
      <div className="lab-toolbar">
        <input className="lab-input grow" placeholder="Search by test, short name or parameter" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="lab-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="lab-select" value={active} onChange={(e) => setActive(e.target.value)}>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
          <option value="">All</option>
        </select>
      </div>
      <div className="lab-page__body">
        <Table columns={columns} rows={rows} loading={loading} onRowClick={(r) => navigate(`/tests/${r._id}`)} empty="No test definitions yet — create one from the global catalogue" />
      </div>
      </>)}

      <FromCatalogueModal open={fromCat} onClose={() => setFromCat(false)} onCreated={(t) => { invalidateTemplates(); toast.success(`${t.name} created`); navigate(`/tests/${t._id}`); }} />
    </Page>
  );
};

// Search the shared TestName / TestPanel dictionary and copy one into the lab
const FromCatalogueModal = ({ open, onClose, onCreated }) => {
  const [tab, setTab] = useState("panels");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) { setTab("panels"); setQ(""); setHits([]); setPicked(null); setName(""); setErr(null); } }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setHits([]); return; }
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const res = tab === "panels" ? await dictionaryApi.searchPanels(q.trim()) : await dictionaryApi.searchTests(q.trim());
        setHits(Array.isArray(res) ? res : res?.data || []);
      } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q, tab, open]);

  const create = async () => {
    if (!picked) return;
    setBusy(true); setErr(null);
    try {
      const body = tab === "panels" ? { panelId: picked.panelId } : { testId: picked.testId };
      if (name.trim()) body.name = name.trim();
      onCreated(await templateApi.fromCatalogue(body));
      onClose();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New definition from the global catalogue"
      footer={
        <>
          {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!picked} loading={busy} onClick={create}>Create &amp; edit</Button>
        </>
      }
    >
      <Tabs tabs={[{ id: "panels", label: "Panels (CBC, Lipid…)" }, { id: "tests", label: "Single tests" }]} value={tab} onChange={(t) => { setTab(t); setPicked(null); setHits([]); }} />
      <input className="lab-input w-full" placeholder={tab === "panels" ? "Search panels, e.g. lipid" : "Search tests, e.g. haemoglobin"} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      <div className="lab-list" style={{ maxHeight: 260 }}>
        {busy && hits.length === 0 && <span className="lab-muted">Searching…</span>}
        {hits.map((h) => {
          const key = h.panelId || h.testId || h._id;
          return (
            <div key={key} className={`lab-list__item ${picked === h ? "selected" : ""}`} onClick={() => { setPicked(h); setName(h.canonicalName || ""); }}>
              <div>
                <div style={{ fontWeight: 600 }}>{h.canonicalName}</div>
                <div className="lab-muted">
                  {tab === "panels"
                    ? `${h.department || "—"}${h.specimenTypes ? ` · ${h.specimenTypes}` : ""}${h.tests?.length ? ` · ${h.tests.length} tests` : ""}`
                    : `${h.unit || ""}${h.testPanel ? ` · ${h.testPanel}` : ""}`}
                </div>
              </div>
              {h.aliases?.length > 0 && <span className="lab-muted">{h.aliases.slice(0, 2).join(", ")}</span>}
            </div>
          );
        })}
        {!busy && q.trim().length >= 2 && hits.length === 0 && <span className="lab-muted">No matches.</span>}
      </div>
      {picked && (
        <Field label="Name in this lab" hint="Parameter codes are generated from names; tidy them up in the editor.">
          <input className="lab-input w-full" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
};

export default Templates;
